import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { getPostAuthPath } from '../utils/userCompany';
import { applyLocale } from '../utils/locale';
import i18n from '../i18n';
import UserAvatar from '../components/UserAvatar';
import ImageCropper from '../components/ImageCropper';
import LoadingSpinner from '../components/LoadingSpinner';
import './UserEdit.css';

const UserEdit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['settings', 'common']);
  const { user, fetchUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const profileImageInputRef = useRef(null);
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailAssigned: null,
    emailClaimed: null,
    emailCompleted: null
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSuccess, setPrefsSuccess] = useState('');
  const [preferredLocale, setPreferredLocale] = useState('en');
  const [languageSaving, setLanguageSaving] = useState(false);
  const [languageSuccess, setLanguageSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phone: user.phone || ''
      });
      setPreferredLocale(user.preferred_locale || 'en');
    }
  }, [user]);

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user?.active_company_id && !user?.company_id) {
        return;
      }

      try {
        const response = await axios.get('/api/notifications/preferences');
        setNotificationPrefs(response.data.data);
      } catch (err) {
        console.error('Failed to load notification preferences', err);
      }
    };

    fetchPreferences();
  }, [user]);

  const isNewUser = user && (!user.first_name || !user.last_name);
  const hasCompany = Boolean(user?.active_company_id || user?.company_id);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedFile) => {
    setProfileImageFile(croppedFile);
    setShowCropper(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setImagePreview(null);
    setProfileImageFile(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const clearPendingImage = () => {
    setImagePreview(null);
    setProfileImageFile(null);
    setRemoveImage(false);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const prefToSelectValue = (value) => {
    if (value === null || value === undefined) {
      return 'default';
    }
    return value ? 'on' : 'off';
  };

  const selectValueToPref = (value) => {
    if (value === 'default') {
      return null;
    }
    return value === 'on';
  };

  const handlePrefChange = (field, value) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [field]: selectValueToPref(value)
    }));
    setPrefsSuccess('');
  };

  const handleSaveLanguage = async () => {
    setLanguageSaving(true);
    setLanguageSuccess('');
    setError('');

    try {
      const payload = new FormData();
      payload.append('preferredLocale', preferredLocale);
      await axios.put(`/api/users/${user.id}`, payload);
      applyLocale(preferredLocale);
      await i18n.changeLanguage(preferredLocale);
      await fetchUser();
      setLanguageSuccess(t('languageSaved'));
    } catch (err) {
      setError(formatError(err) || t('failedSaveLanguage'));
    } finally {
      setLanguageSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setPrefsSaving(true);
    setPrefsSuccess('');
    setError('');

    try {
      const response = await axios.patch('/api/notifications/preferences', notificationPrefs);
      setNotificationPrefs(response.data.data);
      setPrefsSuccess(t('notificationPrefsSaved'));
    } catch (err) {
      setError(formatError(err) || t('failedSaveNotificationPrefs'));
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleRemoveImage = () => {
    setRemoveImage(true);
    setProfileImageFile(null);
    setImagePreview(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = new FormData();
      payload.append('email', formData.email);
      payload.append('firstName', formData.firstName);
      payload.append('lastName', formData.lastName);
      payload.append('phone', formData.phone || '');
      if (formData.password) {
        payload.append('password', formData.password);
      }
      if (removeImage) {
        payload.append('removeProfileImage', 'true');
      } else if (profileImageFile) {
        payload.append('profileImage', profileImageFile);
      }

      await axios.put(`/api/users/${user.id}`, payload);
      setSuccess(t('profileUpdated'));
      await fetchUser();
      setTimeout(async () => {
        try {
          const response = await axios.get('/api/auth/me');
          const updatedUser = response.data.data;
          navigate(getPostAuthPath(updatedUser));
        } catch (error) {
          navigate('/dashboard');
        }
      }, 1500);
    } catch (error) {
      setError(formatError(error) || t('failedUpdateProfile'));
    }
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <div className="user-edit">
      <div className="container main-content user-edit-inner">
        <header className="user-edit-header">
          {!isNewUser && (
            <button
              type="button"
              className="user-edit-back"
              onClick={() => navigate(-1)}
            >
              {t('back')}
            </button>
          )}
          <h1>{t('title')}</h1>
        </header>

        {isNewUser && (
          <div className="user-edit-hint" role="status">
            {t('completeProfileHint')}
          </div>
        )}
        {error && (
          <div className="error user-edit-banner" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="user-edit-success user-edit-banner" role="status">
            {success}
          </div>
        )}

        <div className="user-edit-sections">
          <section className="user-edit-section" aria-labelledby="user-edit-profile-title">
            <h2 id="user-edit-profile-title" className="user-edit-section-title">
              {t('sectionProfile')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <span className="user-edit-field-label">{t('profileImage')}</span>
                <div className="user-edit-avatar-row">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt={t('previewAlt')}
                      className="user-edit-avatar-preview"
                    />
                  ) : (
                    <span
                      className={
                        removeImage ? 'user-edit-avatar-preview--removed' : undefined
                      }
                    >
                      <UserAvatar user={removeImage ? { ...user, profile_image_url: null } : user} size={80} />
                    </span>
                  )}
                  <div>
                    <div className="user-edit-avatar-actions">
                      <input
                        id="user-edit-profile-image"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={profileImageInputRef}
                        className="user-edit-upload-input"
                      />
                      <label
                        htmlFor="user-edit-profile-image"
                        className="user-edit-upload-label"
                      >
                        {t('uploadImage')}
                      </label>
                      {user?.profile_image_url && !removeImage && (
                        <button
                          type="button"
                          className="user-edit-text-btn user-edit-text-btn--danger"
                          onClick={handleRemoveImage}
                        >
                          {t('removeImage')}
                        </button>
                      )}
                      {(imagePreview || removeImage) && (
                        <button
                          type="button"
                          className="user-edit-text-btn"
                          onClick={clearPendingImage}
                        >
                          {t('common:cancel')}
                        </button>
                      )}
                    </div>
                    {removeImage && (
                      <small className="user-edit-remove-note">
                        {t('imageRemovedOnSave')}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              {showCropper && profileImageInputRef.current?.files?.[0] && (
                <ImageCropper
                  imageFile={profileImageInputRef.current.files[0]}
                  onCropComplete={handleCropComplete}
                  onCancel={handleCancelCrop}
                  aspect={1}
                />
              )}

              <div className="user-edit-fields user-edit-fields--profile">
                <div className="form-group">
                  <label htmlFor="user-edit-first-name">{t('firstNameLabel')}</label>
                  <input
                    id="user-edit-first-name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user-edit-last-name">{t('lastNameLabel')}</label>
                  <input
                    id="user-edit-last-name"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user-edit-email">{t('emailLabel')}</label>
                  <input
                    id="user-edit-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user-edit-phone">{t('phoneLabel')}</label>
                  <input
                    id="user-edit-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group user-edit-form-full">
                  <label htmlFor="user-edit-password">{t('newPasswordLabel')}</label>
                  <input
                    id="user-edit-password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    minLength={6}
                  />
                </div>
              </div>

              <div className="user-edit-actions">
                <button type="submit" className="btn btn-primary">
                  {t('updateProfile')}
                </button>
                {!isNewUser && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    {t('common:cancel')}
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="user-edit-section" aria-labelledby="user-edit-language-title">
            <h2 id="user-edit-language-title" className="user-edit-section-title">
              {t('language')}
            </h2>
            <div className="user-edit-language-row">
              <div className="form-group">
                <label htmlFor="preferred-locale">{t('language')}</label>
                <select
                  id="preferred-locale"
                  value={preferredLocale}
                  onChange={(event) => {
                    setPreferredLocale(event.target.value);
                    setLanguageSuccess('');
                  }}
                >
                  <option value="en">{t('languageEnglish')}</option>
                  <option value="sv">{t('languageSwedish')}</option>
                </select>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSaveLanguage}
                disabled={languageSaving}
              >
                {languageSaving ? t('common:saving') : t('saveLanguage')}
              </button>
            </div>
            {languageSuccess && (
              <div className="user-edit-success" role="status">
                {languageSuccess}
              </div>
            )}
          </section>

          {hasCompany && (
            <section
              className="user-edit-section"
              aria-labelledby="user-edit-notifications-title"
            >
              <h2
                id="user-edit-notifications-title"
                className="user-edit-section-title"
              >
                {t('notificationPrefsTitle')}
              </h2>
              <p className="user-edit-section-hint">{t('notificationPrefsHint')}</p>
              <div className="user-edit-fields user-edit-fields--prefs">
                <div className="form-group">
                  <label htmlFor="pref-assigned">{t('prefAssignedLabel')}</label>
                  <select
                    id="pref-assigned"
                    value={prefToSelectValue(notificationPrefs.emailAssigned)}
                    onChange={(e) => handlePrefChange('emailAssigned', e.target.value)}
                  >
                    <option value="default">{t('prefUseDefault')}</option>
                    <option value="on">{t('prefEmailOn')}</option>
                    <option value="off">{t('prefEmailOff')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pref-claimed">{t('prefClaimedLabel')}</label>
                  <select
                    id="pref-claimed"
                    value={prefToSelectValue(notificationPrefs.emailClaimed)}
                    onChange={(e) => handlePrefChange('emailClaimed', e.target.value)}
                  >
                    <option value="default">{t('prefUseDefault')}</option>
                    <option value="on">{t('prefEmailOn')}</option>
                    <option value="off">{t('prefEmailOff')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pref-completed">{t('prefCompletedLabel')}</label>
                  <select
                    id="pref-completed"
                    value={prefToSelectValue(notificationPrefs.emailCompleted)}
                    onChange={(e) => handlePrefChange('emailCompleted', e.target.value)}
                  >
                    <option value="default">{t('prefUseDefault')}</option>
                    <option value="on">{t('prefEmailOn')}</option>
                    <option value="off">{t('prefEmailOff')}</option>
                  </select>
                </div>
              </div>
              <div className="user-edit-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSavePreferences}
                  disabled={prefsSaving}
                >
                  {prefsSaving ? t('common:saving') : t('saveNotificationPrefs')}
                </button>
              </div>
              {prefsSuccess && (
                <div className="user-edit-success" role="status">
                  {prefsSuccess}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserEdit;
